import { useLayoutEffect, useRef, type RefObject } from 'react';
import { cardZoomNow } from '../utils/cardZoom';

type Resize = (id: string, size: { width: number; height: number }) => void;

/** What can change the content's height without changing which elements there are: text, a class, a section opened. */
const WATCHED_CHANGES: MutationObserverInit = {
    childList: true, characterData: true, attributes: true, attributeFilter: ['class', 'open', 'hidden'], subtree: true,
};

/**
 * False while the browser is skipping the element: a node off screen has its body left out of
 * layout (`content-visibility: auto`, see index.css). Measuring it then would make the browser
 * lay it out after all.
 */
const isRendered = (el: Element): boolean =>
    typeof el.checkVisibility !== 'function' || el.checkVisibility({ contentVisibilityAuto: true });

/**
 * Calls `fit` whenever the content under `root` may have changed height: when the root, one of
 * its children or a part marked `details` or `data-fit-part` changes size, when elements or
 * text under it change, and when the browser starts laying the root out again after skipping
 * it. Returns how to stop.
 */
const watchContent = (root: HTMLElement, fit: () => void): (() => void) => {
    // Growing content shows as a taller child of the root; the root itself changes with the node's size
    const sizes = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(() => fit());
    const watched = new Set<Element>();
    const watchParts = () => {
        if (!sizes) return;
        const parts = new Set<Element>([root, ...Array.from(root.children), ...Array.from(root.querySelectorAll('details, [data-fit-part]'))]);
        for (const el of watched) if (!parts.has(el)) { sizes.unobserve(el); watched.delete(el); }
        for (const el of parts) if (!watched.has(el)) { sizes.observe(el); watched.add(el); }
    };
    watchParts();

    const changes = typeof MutationObserver === 'undefined' ? null : new MutationObserver(() => { watchParts(); fit(); });
    changes?.observe(root, WATCHED_CHANGES);

    // Back on screen: whatever changed while the body was skipped is measured now
    const onRendered = (event: Event) => {
        if ((event as ContentVisibilityAutoStateChangeEvent).skipped) return;
        if (event.target instanceof Node && event.target.contains(root)) fit();
    };
    // The event goes to the skipped box, an ancestor of the root: caught on its way down
    document.addEventListener('contentvisibilityautostatechange', onRendered, true);

    return () => {
        sizes?.disconnect();
        changes?.disconnect();
        document.removeEventListener('contentvisibilityautostatechange', onRendered, true);
    };
};

/**
 * Keeps everything in a node in view. The node's content root (`overflow: hidden`, filling
 * the node) is measured when it appears and whenever its content may have changed height;
 * when the content is taller than the node, say a model with more options, the node grows by
 * the difference. It never shrinks the node, so a size the user chose stays; the part that
 * flexes (the prompt field) takes up whatever room is left. A node off screen is not measured
 * until it comes back, and while the canvas is zoomed out and the cards' insides are zoomed up
 * for reading (utils/cardZoom.ts) a node keeps its size, whatever scrolls in it: it is measured
 * again as the canvas comes back.
 */
export const useFitNodeHeight = (
    rootRef: RefObject<HTMLElement>,
    nodeId: string | undefined,
    size: { width: number; height: number } | undefined,
    onResize: Resize | undefined,
) => {
    const latest = useRef({ nodeId, size, onResize });
    latest.current = { nodeId, size, onResize };
    // The height last asked for, so a measure before it lands does not ask again
    const asked = useRef<number | null>(null);
    const watching = useRef<{ root: HTMLElement; stop: () => void } | null>(null);

    // After a render only this is checked: is it still the same root. The measuring is left to
    // the observers, so a render that changes nothing in the node costs nothing here
    useLayoutEffect(() => {
        const root = rootRef.current;
        if (watching.current?.root === root) return;
        watching.current?.stop();
        watching.current = null;
        if (!root) return;

        const fit = () => {
            const { nodeId: id, size: box, onResize: resize } = latest.current;
            if (!id || !box || !resize || !isRendered(root)) return;
            // Grown now, for content zoomed up, the node would keep that room empty once the canvas is back
            if (cardZoomNow() !== 1) return;
            const overflow = root.scrollHeight - root.clientHeight;
            if (overflow <= 1) {
                asked.current = null;
                return;
            }
            const height = Math.ceil(box.height + overflow);
            if (asked.current === height) return;
            asked.current = height;
            resize(id, { width: box.width, height });
        };
        fit();
        watching.current = { root, stop: watchContent(root, fit) };
    });

    useLayoutEffect(() => () => {
        watching.current?.stop();
        watching.current = null;
    }, []);
};
